/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import { service } from "@ember/service";
import type { SerializedSurvey } from "parog-web/models/compound-survey";
import CompoundSurveyCompletedJournalEvent from "parog-web/models/compound-survey-completed-journal-event";

import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import ForcBaseComponent from "parog-web/components/forc/abstract/base-component";
import type { ToastMessage } from "parog-web/components/forc/abstract/journal-event-based-component";
import { Changeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import { validatePresence } from "ember-changeset-validations/validators";
import type { BufferedChangeset, ValidatorMap } from "ember-changeset/types";
import type FlashMessagesService from "ember-cli-flash/services/flash-messages";
import type { IntlService } from "ember-intl";
import type NavigationService from "parog-web/services/navigation";
import type SemeiaSessionService from "parog-web/services/session";

interface Args extends ForcBaseArgs {
  surveyCompletedJe: CompoundSurveyCompletedJournalEvent;
}

export default class CompoundSurveyEditionComponent extends ForcBaseComponent<Args> {
  @service declare store: Store;
  @service declare navigation: NavigationService;
  @service declare session: SemeiaSessionService;
  @service declare flashMessages: FlashMessagesService;
  @service declare intl: IntlService;

  static fullComponentId = "constant-monitoring/edition-form";

  private declare _changeset: BufferedChangeset;

  afterCompleted = this.transitionToDashboard;
  afterCancel = this.transitionToDashboard;

  get questions(): SerializedSurvey {
    return this.args.surveyCompletedJe.surveyRaw;
  }

  get toastMessage(): ToastMessage {
    return this.session.patient
      ? {
          message: "Questionnaire enregistré. Merci !",
          title: "Questionnaire"
        }
      : {
          message: "Information bien enregistrée. Merci !"
        };
  }

  get validations(): ValidatorMap {
    const answersValidations = {};
    this.questions.forEach(q => {
      answersValidations[q.id] = validatePresence({
        presence: true,
        description: "La question"
      });
    });
    const validations = CompoundSurveyCompletedJournalEvent.validations(
      this.intl
    );
    validations["answers"] = answersValidations;
    return validations;
  }

  /**
   * The changeset encapsulate the answers object map
   */
  get changeset(): BufferedChangeset {
    if (this._changeset) return this._changeset;
    this._changeset = Changeset(
      this.args.surveyCompletedJe,
      lookupValidator(this.validations),
      this.validations
    );
    return this._changeset;
  }

  get isDirty() {
    return this.changeset.isDirty;
  }

  async isValid(): Promise<boolean> {
    await this.changeset.validate();
    return this.changeset.get("isValid");
  }

  async save(): Promise<void> {
    await this.changeset.validate();
    this.changeset.execute();
    this.args.surveyCompletedJe.patient ||= this.patient;
    await this.args.surveyCompletedJe.save();
    this.flashMessages.success(this.toastMessage.message, {
      title: this.toastMessage.title
    });
  }

  private transitionToDashboard(): void {
    this.router.transitionTo(
      this.navigation.currentPatientRouteName + ".dashboard"
    );
  }
}
